import { useIntl } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router';
import {
  Header,
  HeaderProps,
  HeaderTitle,
} from '@rossum/components/header/Header';
import {
  Chip,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { getName } from '../../redux/modules/user/helpers';
import { User } from '../../types/user';
import {
  changeFeatureFlagsPath,
  discoveryCallSurveysPath,
  personalInfoPath,
  personalSettingsRoute,
  rossumLabsPath,
} from './routes';

const personalInformationTabName = 'personalInformation';

const personalSettingsTabs = [
  {
    name: personalInformationTabName,
    link: personalInfoPath(),
    translationKey: 'features.personalSettings.title',
  },
  {
    name: 'rossumLabs',
    link: rossumLabsPath(),
    translationKey: 'features.personalSettings.rossumLabs',
  },
  {
    name: 'changeFeatureFlags',
    link: changeFeatureFlagsPath(),
    translationKey: 'features.personalSettings.changeFeatureFlags',
  },
  {
    name: 'discoveryCallSurveys',
    link: discoveryCallSurveysPath(),
    translationKey: 'features.personalSettings.discoveryCallSurveys',
  },
] as const;

type TabName = (typeof personalSettingsTabs)[number]['name'];

const tabsMap = new Map(personalSettingsTabs.map(tab => [tab.name, tab]));

const isValidTabName = (name?: string): name is TabName => {
  return !!(name && tabsMap.has(name as TabName));
};

type Props = {
  user: User;
  tabsVisibility: Partial<Record<TabName, boolean>>;
} & Pick<HeaderProps, 'scrollableDivRef'>;

export const PersonalSettingsHeader = ({
  user,
  tabsVisibility,
  ...params
}: Props) => {
  const intl = useIntl();
  const { push } = useHistory();
  const match = useRouteMatch<{ section: string }>(personalSettingsRoute);
  const matchedSection = match?.params.section;

  const currentSection = isValidTabName(matchedSection)
    ? matchedSection
    : personalInformationTabName;

  const name = getName(user);

  return (
    <Header
      breadcrumbs={null}
      {...params}
      title={hasScrolled => (
        <Stack direction="row" alignItems="center" gap={2}>
          <HeaderTitle
            hasScrolled={hasScrolled}
            title={
              currentSection === personalInformationTabName
                ? name
                : intl.formatMessage({
                    id: `features.personalSettings.${currentSection}`,
                  })
            }
          />
          {(currentSection === 'discoveryCallSurveys' ||
            currentSection === 'changeFeatureFlags') && (
            <Tooltip
              title={intl.formatMessage({
                id: 'features.personalSettings.internalChip.tooltip',
              })}
            >
              <Chip
                label={intl.formatMessage({
                  id: 'features.personalSettings.internalChip',
                })}
                color="transparent"
              />
            </Tooltip>
          )}
        </Stack>
      )}
      description={
        // TODO: this extra stack can be removed after breadcrumbs redesign
        <Stack sx={{ mt: 0.5, mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {currentSection === personalInformationTabName ? (
              <>
                {user.email || user.username}
                {/* extra line break to keep the spacing consistent */}
                <br />
                <br />
              </>
            ) : (
              intl.formatMessage({
                id: `features.personalSettings.${currentSection}.description`,
              })
            )}
          </Typography>
        </Stack>
      }
      tabs={
        <Tabs
          value={currentSection}
          onChange={(_, newValue) => {
            const tab = tabsMap.get(newValue);
            if (tab) {
              push(tab.link);
            }
          }}
        >
          {personalSettingsTabs.map(tab =>
            tabsVisibility[tab.name] === false ? null : (
              <Tab
                key={tab.name}
                data-cy={`personal-settings-${tab.name}`}
                label={intl.formatMessage({ id: tab.translationKey })}
                value={tab.name}
              />
            )
          )}
        </Tabs>
      }
    />
  );
};
